/**
 * Browser-based region inference for guest users.
 *
 * Returns one of: 'NZ' | 'AU' | 'US' | 'UK' | 'CA' | 'global'.
 *
 * Resolution order:
 *   1. Logged-in user's saved region (if it's valid)
 *   2. navigator.language country tag (en-NZ, en-AU, en-GB, en-CA, en-US)
 *   3. navigator.languages[] same tag scan
 *   4. Intl timezone heuristic
 *   5. Provided fallback (default 'global')
 *
 * `FLAG` and `REGION_LABEL_SHORT` are re-exported here for back-compat with
 * existing callers; new code should import them from `./regions` directly.
 */
import {
  isValidRegion,
  FLAG_EMOJI,
  REGION_LABEL_SHORT,
} from './regions';

// Back-compat aliases — existing imports of FLAG/REGION_LABEL_SHORT keep working.
export const FLAG = FLAG_EMOJI;
export { REGION_LABEL_SHORT };

const langToRegion = (lang) => {
  if (!lang) return null;
  const lower = String(lang).toLowerCase();
  if (lower.endsWith('-nz') || lower.includes('-nz-')) return 'NZ';
  if (lower.endsWith('-au') || lower.includes('-au-')) return 'AU';
  if (lower.endsWith('-gb') || lower.endsWith('-uk')) return 'UK';
  if (lower.endsWith('-ca') || lower.includes('-ca-')) return 'CA';
  if (lower.endsWith('-us') || lower.includes('-us-')) return 'US';
  return null;
};

const tzToRegion = () => {
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    if (!tz) return null;
    if (tz === 'Pacific/Auckland' || tz === 'Pacific/Chatham') return 'NZ';
    if (tz.startsWith('Australia/')) return 'AU';
    if (tz === 'Europe/London') return 'UK';
    const caZones = [
      'America/Toronto',
      'America/Vancouver',
      'America/Edmonton',
      'America/Winnipeg',
      'America/Halifax',
      'America/Montreal',
      'America/St_Johns',
      'America/Regina',
    ];
    if (caZones.includes(tz)) return 'CA';
    if (tz.startsWith('America/')) return 'US';
    return null;
  } catch (e) {
    return null;
  }
};

export const detectRegion = (userRegion, fallback = 'global') => {
  if (userRegion && isValidRegion(userRegion)) {
    return userRegion;
  }
  const fromLang = langToRegion(
    typeof navigator !== 'undefined' ? navigator.language : null
  );
  if (fromLang) return fromLang;

  if (typeof navigator !== 'undefined' && Array.isArray(navigator.languages)) {
    for (const l of navigator.languages) {
      const r = langToRegion(l);
      if (r) return r;
    }
  }

  const fromTz = tzToRegion();
  if (fromTz) return fromTz;

  return fallback;
};

export default detectRegion;
